import { Dispatch } from "react";
import { UserInterface } from "../../middleware/interfaces/user/user-interface";
import { UserStateInterface } from "../../middleware/interfaces/user/user-state-interface";
import {
  FindFolderByID,
  UserLogOut,
  PrepareUploadFile,
} from "../general/methods";
import { NavigateFunction } from "react-router-dom";
import axios from "axios";
import { ServerResponseInterface } from "../../middleware/interfaces/server/server-response-interface";
import { UserRetrieveInterface } from "../../middleware/interfaces/user/user-retrieve-interface";
import { UserLastActionInterface } from "../../middleware/interfaces/user/user-last-action-inteface";
import { ProjectContentInterface } from "../../middleware/interfaces/project/project-content-interface";
import { FolderInterface } from "../../middleware/interfaces/folder/folder-interface";
import { UserLocationInterface } from "../../middleware/interfaces/user/user-location-interface";
import { ReportRetrieveInterface } from "../../middleware/interfaces/report/report-retrieve-interface";

const pingInterval = 30000;
export let intervalId: number | null = null;

// the moment of the user's latest real interaction; the session ping sends
// this so the server measures true inactivity instead of time since login
let lastUserActivityDate = new Date();
let activityListenersRegistered = false;

const RegisterUserActivityListeners = () => {
  if (activityListenersRegistered) {
    return;
  }
  activityListenersRegistered = true;
  const updateActivity = () => {
    lastUserActivityDate = new Date();
  };
  window.addEventListener("pointerdown", updateActivity);
  window.addEventListener("pointermove", updateActivity);
  window.addEventListener("keydown", updateActivity);
  window.addEventListener("wheel", updateActivity, { passive: true });
};

export const UpdateUserLastActionDate = (
  user: UserInterface | null,
  dispatch: Dispatch<any>
) => {
  // Clear existing interval
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }

  if (!user) {
    UserLogOut(dispatch);
    return;
  }

  RegisterUserActivityListeners();
  lastUserActivityDate = new Date();

  const UpdateUserStatus = async () => {
    try {
      const serverResponse: any = await PingServerWithLastActionDate({
        ...user,
        lastActionDate: lastUserActivityDate,
      });

      if (
        typeof serverResponse === "string" ||
        serverResponse.header === "afk"
      ) {
        clearInterval(intervalId!);
        intervalId = null;
        UserLogOut(dispatch);
      }
    } catch (error) {
      console.error("Error updating user status:", error);
      clearInterval(intervalId!);
      intervalId = null;
      UserLogOut(dispatch);
    }
  };

  // Start new interval
  intervalId = setInterval(UpdateUserStatus, pingInterval);
};

export const CheckAuthentication = async (dispatch: Dispatch<any>) => {
  const userRetrieveData: UserRetrieveInterface = {
    type: "UserRetrieveInterface",
  };
  dispatch({ type: "RETRIEVE_USER", payload: userRetrieveData });
};

export const HandleInputChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  setData: Dispatch<any>
) => {
  const { name, value } = e.target;
  setData((prevData: any) => ({
    ...prevData,
    [name]: value,
  }));
};

export const HandleDragOver = (
  event: React.DragEvent<HTMLDivElement>,
  setIsHovered: Dispatch<any>
) => {
  event.preventDefault();
  setIsHovered(true);
};

export const HandleDragLeave = (setIsHovered: Dispatch<any>) => {
  setIsHovered(false);
};

export const HandleDrop = (
  event: React.DragEvent<HTMLDivElement>,
  state: UserStateInterface,
  setIsHovered: Dispatch<any>,
  navigate: NavigateFunction,
  dispatch: Dispatch<any>
) => {
  event.preventDefault();
  setIsHovered(false);
  UpdateUserLastActionDate(state.user!, dispatch);
  const uploadFile = event.dataTransfer.files?.[0];
  if (uploadFile) {
    PrepareUploadFile(uploadFile, state.user!, navigate, dispatch);
  }
};

export const HandleLogOut = (dispatch: Dispatch<any>) => {
  UserLogOut(dispatch);
};

export const HandleReturn = (
  state: UserStateInterface,
  dispatch: Dispatch<any>
) => {
  let updatedUser: UserInterface = { ...state.user! };

  if (
    state.user?.openProject &&
    !state.user?.openFolder &&
    !state.user?.openReport
  ) {
    updatedUser.openProject = null;
    dispatch({
      type: "UPDATE_USER",
      payload: updatedUser,
      response: "",
    });
  } else if (state.user?.openFolder) {
    updatedUser.openFolder = FindFolderByID(
      state.user?.openProject?.projectFolders,
      state.user?.openFolder.folderParentID
    );
    dispatch({
      type: "UPDATE_USER",
      payload: updatedUser,
      response: "",
    });
  } else if (state.user?.openReport) {
    updatedUser.openReport = null;
    dispatch({
      type: "UPDATE_USER",
      payload: updatedUser,
      response: "",
    });
    dispatch({ type: "CLOSE_REPORT" });
  }

  // PING SERVER TO UPDATE THE SESSION
  PingServerWithLastActionDate(updatedUser);
};

// Where a level of the canvas breadcrumb leads. The trail reads
// PROJECTS / project / folder / ... (see BuildCanvasCrumbs), and the viewers
// end it with what they show, so the index alone says where to land.
export const SelectCrumbUser = (
  user: UserInterface,
  crumbIndex: number
): UserInterface => {
  let updatedUser: UserInterface = { ...user };

  if (crumbIndex === 0) {
    updatedUser.openProject = null;
    updatedUser.openFolder = null;
  } else {
    const folders: FolderInterface[] = [];
    let folder: FolderInterface | null | undefined = user.openFolder;

    while (folder) {
      folders.unshift(folder);
      folder = FindFolderByID(
        user.openProject?.projectFolders,
        folder.folderParentID
      );
    }

    updatedUser.openFolder = crumbIndex >= 2 ? folders[crumbIndex - 2] : null;
  }

  updatedUser.openReport = null;
  return updatedUser;
};

// Jumps straight to a level of the canvas breadcrumb.
export const HandleCrumbSelect = (
  state: UserStateInterface,
  crumbIndex: number,
  dispatch: Dispatch<any>
) => {
  if (!state.user) {
    return;
  }

  const updatedUser = SelectCrumbUser(state.user, crumbIndex);
  dispatch({
    type: "UPDATE_USER",
    payload: updatedUser,
    response: "",
  });
  if (state.user.openReport) {
    dispatch({ type: "CLOSE_REPORT" });
  }

  // PING SERVER TO UPDATE THE SESSION
  PingServerWithLastActionDate(updatedUser);
};

// The signed-in app writes where the user is into the address, so the
// browser's Back and Forward buttons, a refresh and a copied link all land in
// the same place. LocationHandler keeps the two in step with these.
export const BuildLocationPath = (user: UserInterface): string => {
  if (!user.openProject) {
    return "/DASHBOARD";
  }

  return BuildUserLocationPath({
    projectID: String(user.openProject.projectID),
    folderID: user.openFolder ? String(user.openFolder.folderID) : null,
    reportID: user.openReport ? String(user.openReport) : null,
    type: "UserLocationInterface",
  })!;
};

export const BuildUserLocationPath = (
  location: UserLocationInterface
): string | null => {
  if (location.projectID === undefined) {
    return null;
  }

  if (location.projectID === null) {
    return "/DASHBOARD";
  }

  if (location.reportID) {
    return `/REPORT_VIEWER?project=${location.projectID}&report=${location.reportID}`;
  }

  return location.folderID
    ? `/PROJECT?project=${location.projectID}&folder=${location.folderID}`
    : `/PROJECT?project=${location.projectID}`;
};

// Null for every page outside the signed-in app (the public pages and the
// model viewers), which the address never drives.
export const ReadUserLocation = (
  pathname: string,
  search: string
): UserLocationInterface | null => {
  const route = pathname.toUpperCase().replace(/\/+$/, "");
  const parameters = new URLSearchParams(search);

  if (route === "/DASHBOARD" || route.startsWith("/DASHBOARD/")) {
    return { projectID: null, type: "UserLocationInterface" };
  }

  if (route === "/PROJECT") {
    return {
      projectID: parameters.get("project") ?? undefined,
      folderID: parameters.get("folder"),
      type: "UserLocationInterface",
    };
  }

  if (route === "/REPORT_VIEWER") {
    const reportID = parameters.get("report");

    return {
      projectID: reportID
        ? (parameters.get("project") ?? undefined)
        : undefined,
      reportID: reportID,
      type: "UserLocationInterface",
    };
  }

  return null;
};

// Opens what an address points at. Returns false, leaving the state alone,
// when it is already open or the project isn't one of the user's; a folder or
// report that no longer exists lands on the project's top level instead.
export const ApplyUserLocation = (
  location: UserLocationInterface,
  state: UserStateInterface,
  dispatch: Dispatch<any>
): boolean => {
  if (!state.user || location.projectID === undefined) {
    return false;
  }

  const updatedUser: UserInterface = {
    ...state.user,
    openProject: null,
    openFolder: null,
    openReport: null,
  };

  if (location.projectID !== null) {
    const project = state.user.projects.find(
      (project) => String(project.projectID) === location.projectID
    );

    if (!project) {
      return false;
    }

    updatedUser.openProject = project;

    if (location.reportID) {
      const report = project.projectReports.find(
        (report) => String(report.reportID) === location.reportID
      );
      updatedUser.openReport = report ? report.reportID : null;
    } else if (location.folderID) {
      updatedUser.openFolder =
        project.projectFolders.find(
          (folder) => String(folder.folderID) === location.folderID
        ) ?? null;
    }
  }

  if (BuildLocationPath(updatedUser) === BuildLocationPath(state.user)) {
    return false;
  }

  dispatch({
    type: "UPDATE_USER",
    payload: updatedUser,
    response: "",
  });

  if (
    updatedUser.openReport &&
    String(state.openReport?.reportID) !== String(updatedUser.openReport)
  ) {
    const openReportData: ReportRetrieveInterface = {
      email: updatedUser.email,
      reportID: updatedUser.openReport,
      projectID: updatedUser.openProject!.projectID!,
      type: "ReportRetrieveInterface",
    };
    dispatch({
      type: "RETRIEVE_REPORT",
      payload: openReportData,
    });
  } else if (!updatedUser.openReport && state.openReport) {
    dispatch({ type: "CLOSE_REPORT" });
  }

  // PING SERVER TO UPDATE THE SESSION
  PingServerWithLastActionDate(updatedUser);

  return true;
};

// Everything the project canvas renders for the view the user is currently
// in, already filtered and in display order. The canvas maps over it and the
// page counts it for the head and the empty state, so both agree.
export const CollectVisibleProjectContent = (
  user: UserInterface
): ProjectContentInterface[] => {
  const openProject = user.openProject;

  if (!openProject) {
    return [];
  }

  const contentFilter = openProject.projectContentFilter;
  const visibleContent: ProjectContentInterface[] = [];

  const folderIDs = user.openFolder
    ? user.openFolder.folderChildren
    : openProject.projectFolders
        .filter((folder) => !folder.folderParentID)
        .map((folder) => folder.folderID);

  const fileIDs = user.openFolder
    ? user.openFolder.folderFiles
    : openProject.projectFiles
        .filter((file) => !file.fileParentID)
        .map((file) => file.fileID);

  if (contentFilter?.folders) {
    folderIDs.forEach((folderID) => {
      const folder = openProject.projectFolders.find(
        (f) => f.folderID === folderID
      );
      if (folder) {
        visibleContent.push(folder);
      }
    });
  }

  fileIDs.forEach((fileID) => {
    const file = openProject.projectFiles.find((f) => f.fileID === fileID);
    if (!file) {
      return;
    }
    const isVisible =
      file.fileType === "ifc"
        ? contentFilter?.filesAndModels?.models
        : contentFilter?.filesAndModels?.files;
    if (isVisible) {
      visibleContent.push(file);
    }
  });

  // reports and forges belong to the project itself, never to a folder
  if (!user.openFolder && contentFilter?.reports) {
    openProject.projectReports.forEach((report) => {
      visibleContent.push(report);
    });
  }

  if (!user.openFolder && contentFilter?.forges) {
    openProject.projectForges.forEach((forge) => {
      visibleContent.push(forge);
    });
  }

  return visibleContent;
};

// The trail from the project list down to the open folder, walking up the
// parent chain so nested folders read as a path rather than a bare name.
export const BuildCanvasCrumbs = (user: UserInterface): string[] => {
  const crumbs = ["PROJECTS"];

  if (!user.openProject) {
    return crumbs;
  }

  crumbs.push(user.openProject.projectName);

  const folderNames: string[] = [];
  let folder: FolderInterface | null | undefined = user.openFolder;

  while (folder) {
    folderNames.unshift(folder.folderName);
    folder = FindFolderByID(
      user.openProject.projectFolders,
      folder.folderParentID
    );
  }

  return crumbs.concat(folderNames);
};

export const OpenImageInNewWindow = (src: string) => {
  window.open(src, "_blank");
};

// The public pages frame their images in bordered cells, where a missing file
// reads as a broken layout rather than a missing picture. Dropping the element
// leaves a clean gap instead.
export const HideBrokenImage = (
  event: React.SyntheticEvent<HTMLImageElement>
) => {
  event.currentTarget.style.display = "none";
};

export const StartBrowserDownload = (
  downloadUrl: string,
  downloadFileName?: string
) => {
  const downloadLink = document.createElement("a");
  downloadLink.href = downloadUrl;

  // Only set for locally generated blob URLs, such as an archive the browser
  // bundled itself, where the attribute is the only way to carry a name. A
  // presigned Wasabi URL is cross-origin, so browsers ignore it there and the
  // name comes from the Content-Disposition on the URL instead.
  if (downloadFileName) {
    downloadLink.setAttribute("download", downloadFileName);
  }

  document.body.appendChild(downloadLink);
  downloadLink.click();
  downloadLink.remove();
};

export function OpenPageInNewWindow(
  destinationPage: string,
  data: object = {}
) {
  const reportPageUrl = `${
    window.location.origin
  }/#/${destinationPage}?data=${encodeURIComponent(JSON.stringify(data))}`;
  window.open(reportPageUrl, "_blank");
}

export const StopEventPropagation = (event: React.MouseEvent) => {
  event.stopPropagation();
};

// FormatDateBE produces "YYYY-MM-DD HH:MM:SS" in UTC but without a timezone
// marker; parsing it with new Date() would wrongly treat it as local time
const ToIsoUtcString = (value: Date | string): string => {
  if (value instanceof Date) {
    return value.toISOString();
  }
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(text)) {
    return new Date(text.replace(" ", "T") + "Z").toISOString();
  }
  return new Date(text).toISOString();
};

export const PingServerWithLastActionDate = async (
  user: UserInterface,
  maxRetries: number = 4,
  delay: number = 1000
): Promise<ServerResponseInterface | string> => {
  let serverResponse;

  if (!user) {
    return "SOMETHING WENT WRONG! PLEASE CONTACT ADMIN.";
  }

  const userLastActionData: UserLastActionInterface = {
    lastActionDate: ToIsoUtcString(user.lastActionDate),
    ...(user.openProject != null && {
      openProject: user.openProject.projectID,
    }),
    ...(user.openFolder != null && { openFolder: user.openFolder.folderID }),
    ...(user.openModel != null && { openModel: user.openModel }),
    ...(user.openReport != null && { openReport: user.openReport }),
    type: "UserLastActionInterface",
  };

  const url = "./php/server/session-updater-proxy.php";

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await axios.post(
        url,
        { userLastActionData },
        {
          headers: {
            "Content-Type": "application/json",
            timeout: 5000,
          },
        }
      );
      serverResponse = response.data as ServerResponseInterface;
      return serverResponse;
    } catch (error: any) {
      console.error(
        `Attempt ${attempt} failed:`,
        error.message || error.response?.data
      );
      if (attempt < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      } else {
        serverResponse = error.message || "Failed after multiple attempts.";
      }
    }
  }

  return serverResponse;
};

export function ClearIntervalID() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
}
